<script setup lang="ts">
import MarkdownText from "./internal/MarkdownText.vue";

export interface DetailListItem {
  /** Primary text. The left-hand term in `rows`, the step text in `numbered`. */
  label: string;
  /** Optional second line under the term (`rows` variant). */
  caption?: string;
  /** Right-hand value (`rows` variant). */
  value?: string | number;
}

interface Props {
  /**
   * `rows` (default): label/value pairs separated by dividers.
   * `numbered`: ordered steps, each prefixed with an index badge.
   */
  variant?: "rows" | "numbered";
  /** List entries, shared by both variants. */
  items?: DetailListItem[];
  /** Optional heading shown above the list. */
  title?: string;
}

withDefaults(defineProps<Props>(), {
  variant: "rows",
  items: () => [],
  title: undefined,
});
</script>

<template>
  <div class="pj-detail-list" :class="`pj-detail-list--${variant}`">
    <template v-if="variant === 'numbered'">
      <p v-if="title" class="pj-detail-list__title">{{ title }}</p>
      <ol class="pj-detail-list__steps">
        <li
          v-for="(item, index) in items"
          :key="index"
          class="pj-detail-list__step"
        >
          <span class="pj-detail-list__index" aria-hidden="true">
            {{ index + 1 }}
          </span>
          <MarkdownText
            :content="item.label"
            class="pj-detail-list__step-text"
          />
        </li>
      </ol>
    </template>

    <template v-else>
      <p v-if="title" class="pj-detail-list__title pj-detail-list__title--rows">
        {{ title }}
      </p>
      <dl class="pj-detail-list__rows">
        <div
          v-for="(item, index) in items"
          :key="index"
          class="pj-detail-list__row"
        >
          <dt class="pj-detail-list__term">
            <MarkdownText :content="item.label" class="pj-detail-list__label" />
            <MarkdownText
              v-if="item.caption"
              :content="item.caption"
              class="pj-detail-list__caption"
            />
          </dt>
          <MarkdownText
            v-if="typeof item.value === 'string'"
            :content="item.value"
            class="pj-detail-list__value"
            tag="dd"
          />
          <dd v-else class="pj-detail-list__value">{{ item.value }}</dd>
        </div>
      </dl>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.pj-detail-list {
  box-sizing: border-box;
  width: 100%;
  background: var(--pj-color-brand-white, #ffffff);
  border: 1px solid var(--pj-color-brand-gray-350, #c6c7bf);
  border-radius: var(--pj-radius-xl);
  // Clip the row dividers so they don't overrun the rounded corners.
  overflow: hidden;
}

.pj-detail-list--numbered {
  display: flex;
  flex-direction: column;
  gap: var(--pj-space-4);
  padding: var(--pj-space-4);
}

.pj-detail-list__title {
  margin: 0;
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-base);
  font-weight: var(--pj-font-weight-semibold);
  line-height: 20px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

// In the rows variant the card has no outer padding, so a title needs its own
// padding and a divider to sit flush above the first row.
.pj-detail-list__title--rows {
  padding: var(--pj-space-4);
  border-bottom: 1px solid var(--pj-color-brand-gray-350, #c6c7bf);
}

/* Numbered variant ------------------------------------------------------- */
.pj-detail-list__steps {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--pj-space-4);
}

.pj-detail-list__step {
  display: flex;
  align-items: center;
  gap: var(--pj-space-2);
}

.pj-detail-list__index {
  box-sizing: border-box;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--pj-radius-full);
  background: var(--pj-color-brand-gray-850, #222926);
  color: var(--pj-color-brand-white, #ffffff);
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-sm);
  font-weight: var(--pj-font-weight-semibold);
  line-height: 18px;
}

.pj-detail-list__step-text {
  flex: 1;
  min-width: 0;
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-base);
  font-weight: var(--pj-font-weight-regular);
  line-height: 20px;
  color: var(--pj-color-brand-gray-850, #222926);
}

/* Rows variant ----------------------------------------------------------- */
.pj-detail-list__rows {
  margin: 0;
}

.pj-detail-list__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--pj-space-4);
  padding: var(--pj-space-4);

  &:not(:last-child) {
    border-bottom: 1px solid var(--pj-color-brand-gray-350, #c6c7bf);
  }
}

.pj-detail-list__term {
  display: flex;
  flex-direction: column;
  margin: 0;
  min-width: 0;
}

.pj-detail-list__label {
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-sm);
  font-weight: var(--pj-font-weight-medium);
  line-height: 20px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.pj-detail-list__caption {
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-sm);
  font-weight: var(--pj-font-weight-regular);
  line-height: 18px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.pj-detail-list__value {
  margin: 0;
  text-align: right;
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-base);
  font-weight: var(--pj-font-weight-regular);
  line-height: 20px;
  color: var(--pj-color-brand-gray-550, #74756f);
}
</style>
